import { useState, useEffect, useRef } from "react";
import cstLogo from "../../assets/images/cst_logo_white.png";
import roadReadyLogo from "../../assets/images/road_ready_app_logo.png";
import { ROAD_READY_SITE_URL } from "../../utils/roadReadySite";

const C = {
  navy: "#1B2B4B",
  navyDark: "#101D33",
  gold: "#A8B8C8",
  goldLight: "#FFFFFF",
  white: "#FFFFFF",
};

const navItems = [
  {
    label: "Programs",
    dropdown: [
      { label: "CDL Programs", href: "/programs" },
      { label: "Owner Operator Course", href: "/owner-operator-course" },
      { label: "Dispatcher Course", href: "/dispatcher-course" },
    ],
  },
  { label: "Locations", href: "/locations" },
  {
    label: "🎬 CST Originals",
    badge: "SOON",
    dropdown: [
      { label: "🎬 Reality Show", href: "/reality-show", badge: "SOON" },
      { label: "🎙️ Road Ready Podcast", href: "/podcast", badge: "SOON" },
    ],
  },
  {
    label: "Company",
    dropdown: [
      { label: "About", href: "/about" },
      { label: "Blog", href: "/blog" },
      { label: "Gallery", href: "/gallery" },
      { label: "Commercials", href: "/commercials" },
      { label: "🎬 Reality Show", href: "/reality-show" },
      { label: "🎙️ Road Ready Podcast", href: "/podcast" },
    ],
  },
  {
    label: "Services",
    dropdown: [
      { label: "Financing", href: "/financing" },
      { label: "Veterans", href: "/veterans" },
      { label: "Road Ready Certified", href: "/road-ready-certified" },
      { label: "Donate Equipment", href: "/donate-equipment" },
      { label: "Advertise", href: "/advertise" },
      { label: "Driver Protection", href: "/driver-protection" },
      { label: "Gift Certificates", href: "/gift-certificates" },
    ],
  },
  {
    label: "Tools",
    dropdown: [
      { label: "For Employers", href: "/employer-portal" },
      { label: "Recruiting Network", href: "/recruiting" },
      { label: "Trailer Types Guide", href: "/trailer-types" },
    ],
  },
  { label: "Road Ready Network™", href: ROAD_READY_SITE_URL, badge: "NEW", highlight: true },
  { label: "Contact", href: "/contact" },
];

function SoonBadge() {
  return (
    <span style={{
      fontSize: 8, fontWeight: 800, letterSpacing: "0.12em",
      background: "#E11D48", color: "#fff",
      padding: "2px 5px", borderRadius: 4,
      verticalAlign: "middle", marginLeft: 5,
    }}>SOON</span>
  );
}

function NewBadge() {
  return (
    <span style={{
      fontSize: 8, fontWeight: 800, letterSpacing: "0.12em",
      background: "#F97316", color: "#fff",
      padding: "2px 5px", borderRadius: 4,
      verticalAlign: "middle", marginLeft: 5,
    }}>NEW</span>
  );
}

function DropdownMenu({ items, visible }) {
  return (
    <div style={{
      position: "absolute",
      top: "calc(100% + 16px)",
      left: "50%",
      transform: visible ? "translateX(-50%) translateY(0)" : "translateX(-50%) translateY(-8px)",
      background: "#0d1a2e",
      border: "1px solid rgba(255,255,255,0.25)",
      borderRadius: "8px",
      minWidth: "210px",
      boxShadow: "0 20px 56px rgba(0,0,0,0.7)",
      opacity: visible ? 1 : 0,
      pointerEvents: visible ? "auto" : "none",
      transition: "opacity 0.18s ease, transform 0.18s ease",
      zIndex: 2000,
      overflow: "hidden",
    }}>
      {items.map((item, i) => (
        <a key={item.label} href={item.href} style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "11px 20px",
          fontFamily: "'Urbanist', sans-serif",
          fontSize: "15px", fontWeight: 600,
          color: "rgba(255,255,255,0.72)", textDecoration: "none",
          borderBottom: i < items.length - 1 ? "1px solid rgba(255,255,255,0.06)" : "none",
          transition: "background 0.15s, color 0.15s",
        }}
          onMouseEnter={e => { e.currentTarget.style.background = "rgba(255,255,255,0.12)"; e.currentTarget.style.color = C.goldLight; }}
          onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "rgba(255,255,255,0.72)"; }}
        >
          <span>{item.label}</span>
          {item.badge && <SoonBadge />}
        </a>
      ))}
    </div>
  );
}

export default function Navbar({ topOffset = 0 }) {
  const [openDropdown, setOpenDropdown]     = useState(null);
  const [activePath, setActivePath]         = useState("/");
  const [mobileOpen, setMobileOpen]         = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState(null);
  const [isMobile, setIsMobile]             = useState(false);
  const [scrolled, setScrolled]             = useState(false);
  const timeoutRef = useRef(null);

  useEffect(() => {
    setActivePath(window.location.pathname);
    const check = () => setIsMobile(window.innerWidth <= 768);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > window.innerHeight - 80);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  const handleMouseEnter = (label) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setOpenDropdown(label);
  };
  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => setOpenDropdown(null), 130);
  };
  const isActive = (item) => {
    if (item.href) return activePath === item.href;
    if (item.dropdown) return item.dropdown.some(d => activePath === d.href);
    return false;
  };

  return (
    <>
      <link href="https://fonts.googleapis.com/css2?family=Urbanist:wght@400;600;700;800&display=swap" rel="stylesheet" />
      <style>{`
        @media (max-width: 768px) {
          .cst-nav-desktop { display: none !important; }
        }
        @media (min-width: 769px) {
          .cst-hamburger { display: none !important; }
          .cst-mobile-drawer { display: none !important; }
          .cst-mobile-overlay { display: none !important; }
        }
        .cst-nav-link {
          font-family: 'Urbanist', sans-serif;
          font-size: 16px; font-weight: 600;
          color: rgba(255,255,255,0.68);
          text-decoration: none; letter-spacing: 0.02em;
          white-space: nowrap; background: none; border: none;
          cursor: pointer; padding: 0;
          position: relative; transition: color 0.2s;
          display: flex; align-items: center; gap: 5px;
        }
        .cst-nav-link:hover { color: #FFFFFF; }
        .cst-nav-link.active { color: #FFFFFF; }
        .cst-nav-link.active::after {
          content: ''; position: absolute;
          bottom: -4px; left: 0; right: 0;
          height: 1px; background: #A8B8C8; border-radius: 2px;
        }
        .cst-nav-apply {
          font-family: 'Urbanist', sans-serif;
          font-size: 14px; font-weight: 700;
          letter-spacing: 0.12em; text-transform: uppercase;
          color: #FFFFFF; background: transparent;
          border: 1px solid rgba(255,255,255,0.55);
          padding: 9px 22px; border-radius: 3px;
          text-decoration: none;
          transition: background 0.2s, color 0.2s, border-color 0.2s;
          white-space: nowrap;
        }
        .cst-nav-apply:hover { background: #FFFFFF; color: #071628; border-color: #FFFFFF; }
      `}</style>

      {/* ── Mobile overlay ── */}
      <div
        className="cst-mobile-overlay"
        onClick={() => setMobileOpen(false)}
        style={{
          position: "fixed", inset: 0, zIndex: 1100,
          background: "rgba(0,0,0,0.6)", backdropFilter: "blur(3px)",
          opacity: mobileOpen ? 1 : 0,
          pointerEvents: mobileOpen ? "auto" : "none",
          transition: "opacity 0.25s ease",
        }}
      />

      {/* ── Mobile Drawer ── */}
      <div
        className="cst-mobile-drawer"
        style={{
          position: "fixed", top: 0, right: 0, bottom: 0,
          width: "min(320px, 88vw)", background: "#0a1220",
          zIndex: 1200,
          transform: mobileOpen ? "translateX(0)" : "translateX(100%)",
          transition: "transform 0.3s cubic-bezier(0.4,0,0.2,1)",
          display: "flex", flexDirection: "column", overflowY: "auto",
          boxShadow: "-8px 0 40px rgba(0,0,0,0.7)",
          borderLeft: "1px solid rgba(255,255,255,0.2)",
        }}
      >
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "20px", borderBottom: "1px solid rgba(255,255,255,0.07)", flexShrink: 0,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <a href="/" style={{ display: "flex", alignItems: "center", gap: "10px", textDecoration: "none" }}>
              <img src={cstLogo} alt="CST" style={{ height: "69px", width: "auto" }} />
              <div style={{ lineHeight: 1.2 }}>
                <div style={{ fontFamily: "'Urbanist',sans-serif", fontSize: "11px", fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase", color: C.goldLight }}>California</div>
                <div style={{ fontFamily: "'Urbanist',sans-serif", fontSize: "11px", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "rgba(255,255,255,0.85)" }}>School of Trucking</div>
              </div>
            </a>
            {activePath === "/road-ready-certified" && (
              <>
                <div style={{ width: "1px", height: "32px", background: "rgba(255,255,255,0.2)" }} />
                <img src={roadReadyLogo} alt="Road Ready" style={{ height: "34px", width: "auto", objectFit: "contain" }} />
              </>
            )}
          </div>
          <button onClick={() => setMobileOpen(false)} style={{
            background: "rgba(255,255,255,0.07)", border: "none", borderRadius: "50%",
            width: "36px", height: "36px", cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
            color: "rgba(255,255,255,0.7)", fontSize: "20px", lineHeight: 1, flexShrink: 0,
          }}>✕</button>
        </div>

        <div style={{ flex: 1, padding: "12px 0" }}>
          {navItems.map(item => (
            item.dropdown ? (
              <div key={item.label}>
                <button
                  onClick={() => setMobileExpanded(mobileExpanded === item.label ? null : item.label)}
                  style={{
                    width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between",
                    padding: "14px 20px", background: "none", border: "none", cursor: "pointer",
                    fontFamily: "'Urbanist',sans-serif", fontSize: "16px", fontWeight: 700,
                    color: isActive(item) ? C.goldLight : "rgba(255,255,255,0.85)",
                    textAlign: "left", borderBottom: "1px solid rgba(255,255,255,0.05)",
                  }}
                >
                  <span>{item.label}</span>
                  <svg width="12" height="7" viewBox="0 0 12 7" fill="none"
                    style={{ transform: mobileExpanded === item.label ? "rotate(180deg)" : "none", transition: "transform 0.2s", opacity: 0.5 }}>
                    <path d="M1 1L6 6L11 1" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
                <div style={{
                  maxHeight: mobileExpanded === item.label ? "300px" : "0",
                  overflow: "hidden", transition: "max-height 0.25s ease",
                  background: "rgba(255,255,255,0.03)",
                }}>
                  {item.dropdown.map(sub => (
                    <a key={sub.label} href={sub.href} style={{
                      display: "flex", alignItems: "center", justifyContent: "space-between",
                      padding: "12px 20px 12px 36px",
                      fontFamily: "'Urbanist',sans-serif", fontSize: "15px", fontWeight: 600,
                      color: activePath === sub.href ? C.goldLight : "rgba(255,255,255,0.6)",
                      textDecoration: "none", borderBottom: "1px solid rgba(255,255,255,0.04)",
                    }}>
                      <span>{sub.label}</span>
                      {sub.badge && <SoonBadge />}
                    </a>
                  ))}
                </div>
              </div>
            ) : (
              <a key={item.label} href={item.href} style={{
                display: "flex", alignItems: "center", padding: "14px 20px",
                fontFamily: "'Urbanist',sans-serif", fontSize: "16px", fontWeight: 700,
                color: item.highlight ? "#F97316" : (isActive(item) ? C.goldLight : "rgba(255,255,255,0.85)"),
                textDecoration: "none", borderBottom: "1px solid rgba(255,255,255,0.05)",
              }}>
                {item.label}
                {item.badge && <NewBadge />}
              </a>
            )
          ))}
        </div>

        <div style={{ padding: "20px", flexShrink: 0, borderTop: "1px solid rgba(255,255,255,0.07)" }}>
          <a href="/apply" style={{
            display: "block", textAlign: "center",
            fontFamily: "'Urbanist',sans-serif", fontSize: "14px", fontWeight: 800,
            letterSpacing: "0.1em", textTransform: "uppercase",
            color: "#071628", background: "#FFFFFF",
            padding: "14px 24px", borderRadius: "3px", textDecoration: "none",
          }}>Apply Now</a>
        </div>
      </div>

      {/* ── Full-width flat navbar ── */}
      <nav style={{
        position: "fixed", top: topOffset, left: 0, right: 0, zIndex: 1000,
        display: "flex", alignItems: "center", justifyContent: "space-between",
        background: scrolled ? "rgba(7,22,40,0.97)" : "transparent",
        backdropFilter: scrolled ? "blur(12px)" : "none",
        WebkitBackdropFilter: scrolled ? "blur(12px)" : "none",
        borderBottom: scrolled ? "1px solid rgba(255,255,255,0.06)" : "none",
        boxShadow: scrolled ? "0 4px 24px rgba(0,0,0,0.4)" : "none",
        padding: "0 clamp(20px,5vw,64px)",
        height: "68px",
        transition: "background 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease",
      }}>
        {/* Logo */}
        <div style={{ display: "flex", alignItems: "center", gap: "16px", flexShrink: 0 }}>
          <a href="/" style={{ display: "flex", alignItems: "center", gap: "12px", textDecoration: "none" }}>
            <img src={cstLogo} alt="CST" style={{ height: "53px", width: "auto", objectFit: "contain" }} />
            <div style={{ lineHeight: 1.2 }}>
              <div style={{ fontFamily: "'Urbanist',sans-serif", fontSize: "11px", fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase", color: C.goldLight }}>California</div>
              <div style={{ fontFamily: "'Urbanist',sans-serif", fontSize: "11px", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "rgba(255,255,255,0.8)" }}>School of Trucking</div>
            </div>
          </a>
          {activePath === "/road-ready-certified" && (
            <>
              <div style={{ width: "1px", height: "36px", background: "rgba(255,255,255,0.2)" }} />
              <img src={roadReadyLogo} alt="Road Ready" style={{ height: "40px", width: "auto", objectFit: "contain" }} />
            </>
          )}
        </div>

        {/* Desktop: center nav links */}
        <div className="cst-nav-desktop" style={{ display: "flex", alignItems: "center", gap: "30px" }}>
          {navItems.map(item =>
            item.dropdown ? (
              <div key={item.label} style={{ position: "relative" }}
                onMouseEnter={() => handleMouseEnter(item.label)}
                onMouseLeave={handleMouseLeave}>
                <button className={`cst-nav-link ${isActive(item) ? "active" : ""}`}
                  style={item.badge ? { color: "#C9A84C" } : {}}>
                  {item.label}
                  {item.badge && <SoonBadge />}
                  <svg width="10" height="6" viewBox="0 0 10 6" fill="none"
                    style={{ transition: "transform 0.2s", transform: openDropdown === item.label ? "rotate(180deg)" : "none", opacity: 0.5 }}>
                    <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
                <DropdownMenu items={item.dropdown} visible={openDropdown === item.label} />
              </div>
            ) : (
              <a key={item.label} href={item.href}
                className={`cst-nav-link ${isActive(item) ? "active" : ""}`}
                style={item.highlight ? { color: "#F97316" } : {}}>
                {item.label}
                {item.badge && <NewBadge />}
              </a>
            )
          )}
        </div>

        {/* Desktop: right side */}
        <div className="cst-nav-desktop" style={{ display: "flex", alignItems: "center", gap: "20px", flexShrink: 0 }}>
          <div style={{ width: "1px", height: "20px", background: "rgba(255,255,255,0.1)" }} />
          <a href="/apply" className="cst-nav-apply">Apply Now</a>
        </div>

        {/* Mobile: hamburger */}
        <button
          className="cst-hamburger"
          onClick={() => setMobileOpen(true)}
          aria-label="Open menu"
          style={{
            background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.1)",
            borderRadius: "4px", width: "42px", height: "42px", cursor: "pointer",
            display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "5px",
            flexShrink: 0,
          }}
        >
          <span style={{ display: "block", width: "18px", height: "2px", background: "rgba(255,255,255,0.8)", borderRadius: "2px" }} />
          <span style={{ display: "block", width: "18px", height: "2px", background: "rgba(255,255,255,0.8)", borderRadius: "2px" }} />
          <span style={{ display: "block", width: "12px", height: "2px", background: "rgba(255,255,255,0.8)", borderRadius: "2px", alignSelf: "flex-start", marginLeft: "3px" }} />
        </button>
      </nav>
    </>
  );
}
